import BaseModelLoader from "./BaseModelLoader";
import HandlesLoader from "./cabinetLoader/HandlesLoader";
import CrownMoldingLoader from "./cabinetLoader/CrownMoldingLoader";
import SqftJsonApi from "../../../../../../api/sqftJsonApi";
import {SHAPE_LAYOUTS} from "../../../../../canvas/core/cabinet/cabinetShapes";
import MobileOptimizer from "./MobileOptimizer";
import {
  applyTallCabinetLayout,
  applyTallCabinetSectionHighlights,
} from "./cabinetInterior/tallCabinetInteriorLayoutApplier";
import {
  applyTallCabinetHeight,
  applyTallCabinetMaterialUvProjection,
  applyTallCabinetWidthDepth,
} from "./tallCabinetGeometry/tallCabinetGeometryResizer";
import { applyDoorStateForInstance } from "./cabinetInterior/tallCabinetDoorsAnimator";
import { getCabinetSectionsFromScene } from "./cabinetInterior/cabinetSectionsParser";
import workspace from "../../../../../canvas/core/workspace";
import SqftStore from "../../../../../canvas/store/sqftStore";
import { SHAPE_PART_ID } from "../../../../../../constants";
import { MINIMUM_TALL_CABINET_HEIGHT } from "../../../../../../helpers/cabinetBoxCompatibilityHelper";
import {
  getCabinetInteriorHeight,
  hasEditableCabinetInteriorLayout,
} from "../../../../../../helpers/tallCabinetInteriorHelper";
import {
  buildLayoutFromGlbSections,
} from "../../../../../navigation/menuRight/Attributes/helpers/cabinetInteriorLayoutHelper";
import {
  CABINET_INTERIOR_SECTION_TYPE as SECTION_TYPE
} from "../../../../../../constants/cabinetInteriorConstants";
import {createSpacerModel, getSpacerGeometryParts} from "./cabinetLoader/spacerGeometryFactory";

class CabinetLoader extends BaseModelLoader {
  constructor(opts) {
    super(opts);
    this.handlesLoader = new HandlesLoader(opts.scene, opts.loader);
    this.crownMoldingLoader = new CrownMoldingLoader(opts.scene, opts.loader, opts.modelScale);

    // Mobile optimization utility
    this.mobileOptimizer = new MobileOptimizer();
  }

  _isTallCabinet(data) {
    if (data.partId === SHAPE_PART_ID.tall_cabinet) {
      return true;
    }

    return data.height > MINIMUM_TALL_CABINET_HEIGHT;
  }

  _isSpacer(data) {
    return Boolean(data.is_filler || data.is_spacer);
  }

  _isFallbackInteriorLayout(layout, glbSections) {
    if (!layout || !Array.isArray(layout.sections) || layout.sections.length !== 1) {
      return false;
    }

    const section = layout.sections[0];
    const modelHasDrawers = glbSections.some((modelSection) => modelSection.type === SECTION_TYPE.drawer);

    return modelHasDrawers
      && section.type === SECTION_TYPE.box
      && section.hasDoor !== false
      && (!section.quantity || section.quantity === 0);
  }

  /**
   * Check if data represents an appliance model
   * @param {Object} data
   * @returns {boolean}
   */
  _isAppliance(data) {
    return data.partId && (
      data.partId.includes('appliance') ||
      data.partId.includes('refrigerator') ||
      data.partId.includes('dishwasher') ||
      data.partId.includes('stove') ||
      data.partId.includes('microwave') ||
      data.partId.includes('oven') ||
      data.partId.includes('range')
    );
  }

  /**
   * Override load method with appliance-specific handling
   * @param {Object} data
   * @returns {Promise}
   */
  async load(data, shouldContinue = () => true) {
    const key = this._makeKey(data);

    if (this.instances.has(key)) {
      return this.instances.get(key);
    }

    if (this._isSpacer(data)) {
      const spacerInstance = await this.loadProceduralSpacer(data, key, shouldContinue);
      if (!shouldContinue()) {
        return null;
      }
      if (spacerInstance) {
        return spacerInstance;
      }
    }

    const url = await this.getUrl(data);

    if (!url) {
      console.error("url was not loaded", data);
      return null
    }

    // Try to get mobile-optimized version for appliances
    const optimizedUrl = this._isAppliance(data) && this.mobileOptimizer.shouldOptimize() ?
      this.mobileOptimizer.getMobileOptimizedUrl(url) : url;

    let raw = this.rawCache.get(optimizedUrl);
    if (!raw) {
      try {
        raw = await this._loadGltf(optimizedUrl);
      } catch (error) {
        // Fallback to original URL if mobile version fails
        if (optimizedUrl !== url) {
          console.log('Mobile version not found, loading regular version');
          raw = await this._loadGltf(url);
        } else {
          throw error;
        }
      }

      // Apply mobile optimizations before caching
      if (this._isAppliance(data)) {
        await this.mobileOptimizer.optimizeModel(raw);
      }

      // Don't cache on mobile to save memory
      if (!this.mobileOptimizer.shouldOptimize()) {
        this.rawCache.set(optimizedUrl, raw);
      }
    }

    const instance = await this.clone(raw, data);
    if (!shouldContinue()) {
      return null;
    }

    // Additional instance-level optimizations for appliances
    if (this._isAppliance(data)) {
      this.mobileOptimizer.optimizeInstance(instance);
    }

    this.scene.add(instance);
    this.instances.set(key, instance);

    // Trigger garbage collection after appliance load on mobile
    if (this._isAppliance(data)) {
      this.mobileOptimizer.triggerGarbageCollection();
    }

    return instance;
  }

  async loadProceduralSpacer(data, key, shouldContinue) {
    const parts = getSpacerGeometryParts(data);
    if (!parts) {
      return null;
    }

    const materialUrl = data.material_url || data.url;
    let material = null;
    if (materialUrl) {
      try {
        material = await this._loadMaterial(materialUrl);
      } catch (error) {
        void error;
      }
    }

    if (!shouldContinue()) {
      return null;
    }

    if (!material) {
      material = new window.THREE.MeshPhysicalMaterial({
        color: new window.THREE.Color(1, 1, 1),
        roughness: 0.62,
      });
    }

    const shape = workspace.getShapeById(data.shapeId);
    const instance = createSpacerModel({
      data,
      material,
      modelScale: this.modelScale,
      shape,
      parts,
    });
    if (!instance) {
      return null;
    }

    const position = this.getModelPosition(data);
    instance.position.set(position.x, position.y, position.z);
    if (data.rotation) {
      instance.rotation.y = window.THREE.MathUtils.degToRad(data.rotation);
    }

    instance.userData.isProceduralSpacer = true;
    this._addUserData(instance, data, key);
    this.scene.add(instance);
    this.instances.set(key, instance);
    return instance;
  }

  /**
   * Tall cabinets has live resizing and removed from api resizing
   * because of the artifacts in the 3d
   * if cabinet was resized then we use API to resize model
   * @param data
   * @return {Promise<*>}
   */
  async getUrl(data) {
    if (this._isTallCabinet(data) && !this._isSpacer(data)) {
      return data.url;
    }

    if (data.was_resized || data.framed || this._isSpacer(data)) {
      // Set points to 0 rotation
      const original_points = data.original_points;
      const contour_points = data.contour_points;
      data.original_points = data.points_on_zero_rotation.original_points;
      data.contour_points = data.points_on_zero_rotation.contour_points;

      let resized = null;
      try {
        resized = await SqftJsonApi[
          data.shapeLayout === SHAPE_LAYOUTS.rectangle
            ? 'getResizedCabinet'
            : 'getResizedCornerCabinet'
          ](data, {});
      } catch (e) {
        console.error('[CabinetLoader] getUrl API error, skipping cabinet:', e);
      }

      // Restore rotated points
      data.original_points = original_points;
      data.contour_points = contour_points;

      if (resized && resized.s3Url) {
        return resized.s3Url;
      }
    }
    return data.url;
  }

  /**
   * Applies tall-cabinet geometry and the optional editable-interior layout.
   * Legacy models keep their filter-based quantities and never receive a fallback layout.
   */
  processTallCabinetGeometry(inst, data) {
    const shape = workspace.getShapeById(data.shapeId);
    // A stale preview load can finish after its temporary workspace shape was
    // cleared; real cabinet reloads will still have a shape here.
    if (!shape) {
      return false;
    }

    const cabinetHeight = shape.getHeight();
    const cabinetInteriorHeight = getCabinetInteriorHeight(cabinetHeight);
    let cabinetInteriorLayout = shape.shapeCabinet.getCabinetInteriorLayout();
    let hasEditableInteriorLayout = hasEditableCabinetInteriorLayout(cabinetInteriorLayout);
    const glbSections = getCabinetSectionsFromScene(inst);
    const modelSupportsEditableInterior = Boolean(glbSections && glbSections.length);
    const shouldRestoreModelLayout = modelSupportsEditableInterior
      && this._isFallbackInteriorLayout(cabinetInteriorLayout, glbSections);

    inst.userData.shapeHeight = cabinetHeight;
    inst.userData.cabinetInteriorHeight = cabinetInteriorHeight;

    if (modelSupportsEditableInterior && (!hasEditableInteriorLayout || shouldRestoreModelLayout)) {
      cabinetInteriorLayout = buildLayoutFromGlbSections(glbSections, cabinetInteriorHeight);
      hasEditableInteriorLayout = true;
      shape.shapeCabinet.setCabinetInteriorLayout(cabinetInteriorLayout);
      SqftStore.updateCalculations();
    } else if (!modelSupportsEditableInterior
      && cabinetInteriorLayout
      && !hasEditableInteriorLayout
    ) {
      // Older builds stored a one-section fallback after opening 3D. Remove it
      // so legacy tall cabinets continue to use their product filters.
      cabinetInteriorLayout = null;
      shape.shapeCabinet.setCabinetInteriorLayout(null);
      SqftStore.updateCalculations();
    }

    applyTallCabinetHeight(inst, shape);
    if (hasEditableInteriorLayout) {
      applyTallCabinetLayout(inst, {
        ...cabinetInteriorLayout,
        doorExtensionEnabled: shape.shapeCabinet.getDoorExtensionEnabled(),
      });
      applyDoorStateForInstance(inst);
      applyTallCabinetSectionHighlights(inst, cabinetInteriorLayout);
    }
    applyTallCabinetWidthDepth(inst, shape);
    applyTallCabinetMaterialUvProjection(inst, shape);

    return true;
  }

  /**
   * Simplified post processing for mobile appliances
   * @param inst
   * @param data
   * @return {Promise<void>}
   */
  async postProcess(inst, data) {
    // Drag previews only need the base GLB shell. Handles, crown molding, and
    // tall-cabinet section layout are committed after the real shape is dropped.
    if (data.isCabinetDragPreview) {
      inst.userData.shapeId = data.shapeId;
      return;
    }

    const isTallCabinet = this._isTallCabinet(data);

    if (data.material_url) {
      const mat = this.materialCache[data.material_url];
      await this.crownMoldingLoader.loadCrownMolding(data.shapeId, mat);
    }

    inst.userData.shapeId = data.shapeId;

    if (isTallCabinet && !this.processTallCabinetGeometry(inst, data)) {
      return;
    }

    // Handles are accessories, so add them only after cabinet geometry and
    // optional tall-cabinet section layout have reached their final transforms.
    const hasHandles = data.handles && data.handles.length;
    if (hasHandles) {
      await this.handlesLoader.loadHandles(data, inst);
    }
  }

  remove(data) {
    const instance = this.instances.get(this._makeKey(data));
    // Procedural spacers own unique ExtrudeGeometry that is not shared by the GLB cache.
    // Dispose only that geometry here; their materials can still be shared with other cabinets.
    if (instance && instance.userData.isProceduralSpacer) {
      instance.traverse((child) => {
        if (child.isMesh && child.geometry) {
          child.geometry.dispose();
        }
      });
    }

    this.crownMoldingLoader.removeForShape(data.shapeId);
    super.remove(data);
  }

  /**
   * Get current optimization status
   * @returns {Object}
   */
  getOptimizationStatus() {
    return {
      totalInstances: this.instances.size,
      mobileOptimizer: this.mobileOptimizer.getSettings()
    };
  }
}

export default CabinetLoader;
